/**
 * 화면으로 가는 **유일한 두 방법** — `<ScreenLink>` 와 `useScreenNavigate()`.
 *
 * ─── react-router 의 `Link`·`useNavigate` 를 재export 하지 않는다 ───
 * 그것들은 **경로 문자열**을 받는다. 재export 하는 순간 vertical 코드에
 * `<Link to="/pay/runs">` 가 등장할 수 있고, 그 문자열은 선언과 아무 관계가 없다 —
 * 화면 경로가 바뀌면 그 링크는 조용히 404 가 되고 어떤 게이트도 그것을 보지 못한다.
 * 여기서는 **화면 키**만 받는다. 경로는 매니페스트에서 온다.
 * (`orpc-only-in-rpc-kernel` 이 `os` 를 숨긴 것과 같은 수법이고,
 *  `react-router-only-in-core-web-and-sdk` 가 우회를 막는다.)
 *
 * ─── 매니페스트에 없는 화면을 링크하면 ───
 * 그 사용자에게 그 화면은 **없다**(권한이 없거나 모듈이 꺼졌다). 링크를 그리면 클릭 → 404 다.
 * 그래서 링크가 아니라 비활성 텍스트를 그린다. 던지지 않는 이유: 목록 한 줄의 링크 때문에
 * 화면 전체가 죽는 것은 비례가 안 맞는다. 선언 불일치(로더/라우트 누락)는 CI 의
 * `web:check` 가 잡고, 여기 오는 것은 **정상적인 권한 차이**다.
 */
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { fillScreenPath, screenOf } from '@worhythm/contracts';
import { useManifest } from './context.js';

export type ScreenParams = Readonly<Record<string, string>>;

export interface ScreenLinkProps {
  /** `<moduleKey>.<rest>` — 선언한 화면 키. 경로가 아니다. */
  screen: string;
  params?: ScreenParams;
  className?: string;
  children: ReactNode;
}

export function ScreenLink({ screen, params, className, children }: ScreenLinkProps) {
  const manifest = useManifest();
  const target = screenOf(manifest, screen);
  if (!target) {
    return (
      <span className={className} aria-disabled="true" style={{ color: 'var(--wr-fg-muted)' }}>
        {children}
      </span>
    );
  }
  return (
    <Link className={className} to={fillScreenPath(target.path, params)}>
      {children}
    </Link>
  );
}

/**
 * 코드에서 이동. 폼 저장 후 목록으로 돌아가는 자리에 쓴다.
 *
 * 모르는 화면 키에는 **던진다** — 링크와 다른 판정인 이유: 링크는 렌더 중의 권한 차이지만,
 * 이 호출은 사용자가 방금 누른 버튼의 결과다. 아무 일도 안 일어나는 버튼은 사용자가
 * 같은 버튼을 다시 누르게 만들고(중복 제출), 그 상태는 로그도 안 남는다.
 */
export function useScreenNavigate(): (screen: string, params?: ScreenParams) => void {
  const manifest = useManifest();
  const navigate = useNavigate();
  return (screen, params) => {
    const target = screenOf(manifest, screen);
    if (!target) {
      throw new Error(
        `이동하려는 화면 '${screen}' 이 이 사용자의 매니페스트에 없다. ` +
          `권한이 없거나(정상) 화면 키가 틀렸다(선언 불일치 — web:check 가 잡는다).`,
      );
    }
    void navigate(fillScreenPath(target.path, params));
  };
}
